// filepath: /Users/ogabrielisboa/Documents/dev/backoffice-seminovos/frontend/src/pages/BuyerDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api';

// Shadcn UI Components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Spinner } from '../components/ui/spinner';

const G = (obj, path) => path.split('.').reduce((o, k) => o?.[k], obj) ?? '-';

export default function BuyerDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => { 
    api.get(`/compradores/${id}`).then(r => setData(r.data)); 
  }, [id]);

  if (!data) {
    return (
      <div className="container mx-auto px-4 mt-4 mb-6">
        <div className="min-h-[400px] flex flex-col items-center justify-center gap-4 bg-background border rounded-lg p-6">
          <Spinner className="h-10 w-10 text-primary" />
          <p className="text-muted-foreground">
            {t('pages.buyerDetail.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <Card className="overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h1 className="text-3xl font-bold tracking-tight">
              {data.nomeFantasia}
            </h1>
            
            <Button 
              asChild
              size="lg"
              className="font-semibold"
            >
              <RouterLink to={`/admin/compradores/${id}/edit`}>
                {t('pages.buyerDetail.editButton')}
              </RouterLink>
            </Button>
          </div>
        </div>
        
        <div className="p-6">
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList className="mb-2">
              <TabsTrigger value="basic">{t('pages.buyerDetail.basicInfo')}</TabsTrigger>
              <TabsTrigger value="representatives">{t('buyerForm.representatives')}</TabsTrigger>
              <TabsTrigger value="operational">{t('buyerForm.tabs.operational')}</TabsTrigger>
            </TabsList>

            <TabsContent value="basic">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {[
                  [t('pages.buyerDetail.companyName'), 'nomeFantasia'], 
                  [t('pages.buyerDetail.legalName'), 'razaoSocial'],
                  [t('pages.buyerDetail.cnpj'), 'cnpj'], 
                  [t('buyerForm.fields.inscricaoEstadual'), 'inscricaoEstadual'],
                  [t('buyerDetail.groupCompany'), 'grupoEmpresarial']
                ].map(([label, path]) => (
                  <div key={path} className="p-4 bg-muted/40 rounded-md border">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                      {label}
                    </span>
                    <p className="mt-1 font-medium">
                      {G(data, path)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">{t('pages.buyerDetail.address')}</h3>
                <Card className="p-6 bg-muted/40">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                    {[
                      [t('pages.buyerDetail.zipCode'), 'endereco.cep', 3], 
                      [t('buyerForm.fields.logradouro'), 'endereco.logradouro', 5],
                      [t('buyerForm.fields.numero'), 'endereco.numero', 2], 
                      [t('buyerForm.fields.complemento'), 'endereco.complemento', 2],
                      [t('buyerForm.fields.bairro'), 'endereco.bairro', 3], 
                      [t('pages.buyerDetail.city'), 'endereco.cidade', 4], 
                      [t('pages.buyerDetail.state'), 'endereco.estado', 2]
                    ].map(([label, path, cols]) => (
                      <div key={path} className={`col-span-${cols}`}>
                        <span className="text-xs text-muted-foreground block mb-1">
                          {label}
                        </span>
                        <p className="font-medium">
                          {G(data, path)}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">{t('buyerForm.fields.contato')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  {[
                    {key: 'nome', label: t('buyerForm.fields.nome')},
                    {key: 'telefone', label: t('buyerForm.fields.telefone')},
                    {key: 'celular', label: t('buyerForm.fields.celular')},
                    {key: 'email', label: t('buyerForm.fields.email')}
                  ].map(({key, label}) => (
                    <div key={key}>
                      <span className="text-xs text-muted-foreground block mb-1">
                        {label}
                      </span>
                      <p className="font-medium">
                        {G(data, `contatoPrincipal.${key}`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="representatives">
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">{t('buyerForm.representatives')}</h3>
                {data.representantes?.length ? 
                  data.representantes.map((rep, index) => (
                    <Card 
                      key={index} 
                      className="mb-4 p-6 bg-muted/40 hover:border-primary transition-colors"
                    >
                      <h4 className="text-base font-semibold text-primary mb-4">
                        {rep.nome || '-'}
                      </h4>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                          [t('buyerForm.fields.departamento'), rep.departamento],
                          [t('buyerForm.fields.celular'), rep.celular],
                          [t('buyerForm.fields.email'), rep.email]
                        ].map(([label, value], idx) => (
                          <div key={idx}>
                            <span className="text-xs text-muted-foreground block mb-1">
                              {label}
                            </span>
                            <p className="font-medium">
                              {value || '-'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))
                  : (
                    <p className="text-muted-foreground">
                      {t('buyerDetail.noRepresentatives')}
                    </p>
                  )
                }
              </div>
            </TabsContent>

            <TabsContent value="operational">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {[
                  [t('buyerDetail.segment'), 'dadosOperacionais.segmento'],
                  [t('buyerDetail.partnershipStart'), 'dadosOperacionais.inicioParceria'],
                  [t('buyerDetail.observations'), 'dadosOperacionais.observacoes']
                ].map(([label, path]) => (
                  <div 
                    key={path} 
                    className={`p-4 bg-muted/40 rounded-md border ${
                      path === 'dadosOperacionais.observacoes' ? 'col-span-full' : ''
                    }`}
                  >
                    <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                      {label}
                    </span>
                    <p className={`mt-1 font-medium ${
                      path === 'dadosOperacionais.observacoes' ? 'whitespace-pre-wrap' : ''
                    }`}>
                      {G(data, path)}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Card>
    </div>
  );
}
